/** @format */
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    EventEmitter,
    Inject,
    input,
    Optional,
    Output,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { Attainment, CourseUnit, CourseUnitAttainment, CourseUnitSelection, OtmId } from 'common-typescript/types';
import * as _ from 'lodash-es';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { PLAN_ACTIONS_SERVICE_INJECTION_TOKEN, PlanActionsService } from '../../plan/plan-actions-service/plan-actions.service';
import { PlanManager, RemovedSubstitution } from '../../plan/plan-manager/plan-manager.service';
import { AttainmentEntityService } from '../../service/attainment-entity.service';
import { CourseUnitEntityService } from '../../service/course-unit-entity.service';
import { CourseUnitStateObject, PlanData, PlanStateObject } from '../../service/plan-state.service';
import { UniversityService } from '../../service/university.service';
import { Action, InputType } from '../../study-boxes/study-action-box/study-action-box.component';
import { PlanStructureContext } from '../plan-structure-context';

@Component({
    selector: 'sis-plan-structure-course-unit',
    templateUrl: './plan-structure-course-unit.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class PlanStructureCourseUnitComponent {
    readonly courseUnit = input.required<CourseUnit>();
    readonly headingLevel = input.required<number>();
    readonly selected = input<boolean>();
    readonly action = input<Action>();
    readonly inputType = input<InputType>();
    readonly actionInputId = input<string>();
    readonly actionInputName = input<string>();
    readonly nameClickable = input<boolean>(true);
    readonly showCompletionMethodSelection = input<boolean>(false);
    readonly showAttainment = input<boolean>(true);
    @Output() actionChange? = new EventEmitter<OtmId>();

    protected readonly courseUnitStateObject = computed<CourseUnitStateObject | undefined>(
        () => this.planStructureContext.planStateObject().courseUnits?.[this.courseUnit().id],
    );

    protected readonly attainment = computed<Attainment | undefined>(() => {
        const courseUnitStateObject = this.courseUnitStateObject();
        return courseUnitStateObject ? this.planStructureContext.planData().attainmentsById[courseUnitStateObject.attainmentId] : undefined;
    });

    protected readonly isSubstituted = computed(() => this.attainment()?.state === 'SUBSTITUTED');
    protected readonly invalidSelection = computed(() => this.courseUnitStateObject()?.invalidSelectionWithoutApproval);
    protected readonly isAboutToExpire = computed(() => this.courseUnitStateObject()?.isAttainmentAboutToExpire);
    protected readonly newerVersionAvailable = computed(() => this.courseUnitStateObject()?.newerVersionAvailable ?? false);

    protected readonly isCrossStudyCourseUnit = computed(() => {
        const courseUnit = this.courseUnit();
        const isCourseUnitsOrganisationSameAsStudents = courseUnit.universityOrgIds?.includes(
            this.universityService.getCurrentUniversityOrgId(),
        );
        return !isCourseUnitsOrganisationSameAsStudents && courseUnit.cooperationNetworkDetails?.networks.length > 0;
    });

    protected readonly studyState = computed<'DRAFT' | 'DELETED' | undefined>(() => {
        const { documentState } = this.courseUnit();
        return documentState !== 'ACTIVE' ? documentState : undefined;
    });

    /** A new check whenever the course unit, its state or the plan data changes, as the template subscribes to it */
    protected readonly hasSelectableSubstitution$ = computed<Observable<boolean>>(() =>
        this.computeHasSelectableSubstitution(
            this.courseUnit(),
            this.courseUnitStateObject(),
            this.attainment(),
            this.planStructureContext.planStateObject(),
            this.planStructureContext.planData(),
        ),
    );

    /** The plan manager keeps no signals, so a new plan state is what tells that the removed substitution may have changed */
    protected readonly removedSubstitution = computed<RemovedSubstitution | undefined>(() => {
        this.planStructureContext.planStateObject();
        return this.planManager?.getRemovedSubstitution(this.courseUnit().id);
    });

    private readonly planStructureContext = inject(PlanStructureContext);

    constructor(
        @Optional() @Inject(PLAN_ACTIONS_SERVICE_INJECTION_TOKEN) private planActionsService: PlanActionsService,
        // Provided by the plan edit dialogs; absent on the plan page
        @Optional() private planManager: PlanManager,
        private universityService: UniversityService,
        private readonly courseUnitEntityService: CourseUnitEntityService,
        private readonly attainmentEntityService: AttainmentEntityService,
    ) {}

    openCourseUnitInfoModal() {
        this.planActionsService?.openCourseUnitInfoModal(this.courseUnit());
    }

    openSelectSubstitutionModal() {
        this.planActionsService?.openSelectSubstitutionModal?.(this.courseUnit());
    }

    private computeHasSelectableSubstitution(
        courseUnit: CourseUnit,
        courseUnitStateObject: CourseUnitStateObject | undefined,
        ownAttainment: Attainment | undefined,
        planStateObject: PlanStateObject,
        planData: PlanData,
    ): Observable<boolean> {
        const substitutionOptions = courseUnit.substitutions ?? [];
        if (
            !substitutionOptions.length ||
            ownAttainment ||
            !courseUnitStateObject?.isInPlan ||
            courseUnitStateObject?.isInPlanAsSubstitute
        ) {
            return of(false);
        }
        const groupIds = [...new Set(substitutionOptions.flat().map(s => s.courseUnitGroupId))];
        return this.courseUnitEntityService.getByGroupIds(groupIds).pipe(
            map(substituteCourseUnits => {
                const substituteByGroupId = _.keyBy(substituteCourseUnits, 'groupId');
                const currentUniversityOrgId = this.universityService.getCurrentUniversityOrgId();
                const allAttainments = _.values(planData.attainmentsById);
                const courseUnitAttainmentByGroupId: { [key: OtmId]: CourseUnitAttainment } = _.keyBy(
                    allAttainments.filter((a: Attainment) => a.type === 'CourseUnitAttainment') as CourseUnitAttainment[],
                    'courseUnitGroupId',
                );
                // Only an attached attainment disqualifies a substitute — an unattached completed course unit is still selectable.
                const isAttainmentAlreadyAttached = (groupId: OtmId): boolean => {
                    const attainment = courseUnitAttainmentByGroupId[groupId];
                    return !!attainment && this.attainmentEntityService.isAttached(attainment, allAttainments);
                };
                const isAlreadyInPlanAsRegularSelection = (substituteCourseUnit: CourseUnit): boolean => {
                    const stateObject = planStateObject.courseUnits[substituteCourseUnit.id];
                    // Already being someone's substitute doesn't disqualify it — only a direct selection does.
                    return !!stateObject?.isInPlan && !stateObject?.isInPlanAsSubstitute;
                };
                return substitutionOptions.some(option =>
                    option.every(s => {
                        const substituteCourseUnit = substituteByGroupId[s.courseUnitGroupId];
                        return (
                            !!substituteCourseUnit &&
                            !isAttainmentAlreadyAttached(s.courseUnitGroupId) &&
                            !isAlreadyInPlanAsRegularSelection(substituteCourseUnit) &&
                            !!substituteCourseUnit.universityOrgIds?.includes(currentUniversityOrgId)
                        );
                    }),
                );
            }),
            // Fail closed: hide the button rather than show an error for this background check.
            catchError(() => of(false)),
        );
    }

    getCourseUnitSelection(courseUnitId: OtmId): CourseUnitSelection {
        return this.planStructureContext.planStateObject().courseUnits?.[courseUnitId]?.courseUnitSelection;
    }
}
